import { MultipleTagItem } from '@components/ArtBoard/Node/components/NodeTags/MultipleTagItem'
import {
  TagsPopover,
  useTagsGuard,
} from '@components/ArtBoard/Node/components/TagsPopover'
import { TagBadge } from '@components/TagBadge'
import type { TagItem } from '@octopus-do/octopus-project-core'
import { TinyPopover } from '@octopus-do/octopus-storybook'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { getUsedTags } from '@store/reducers/tags/selector'
import cx from 'classnames'
import type React from 'react'
import { forwardRef, useEffect, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { useProjectAccess } from '@/hooks'
import { TAGS_PERMISSION } from '@/permissions/project/definitions'
import type { AppState } from '@/types'
import css from './NodeTags.module.scss'

type Props = {
  id: string
}

interface TagsProps {
  tags: TagItem[]
  colorHex: string
  hasAccess: boolean
}

const SingleTag: React.FC<TagsProps> = ({ tags, colorHex, hasAccess }) => {
  if (tags.length !== 1) {
    return null
  }

  return (
    <button
      className={cx(css.single, {
        [css.cursorDefault]: !hasAccess,
      })}
      type="button"
    >
      <TagBadge title={tags[0].title} color={colorHex} />
    </button>
  )
}

const MultipleTags: React.FC<Pick<TagsProps, 'tags'>> = ({ tags }) => {
  if (tags.length <= 1) {
    return null
  }

  return (
    <>
      {tags.map((tag, index) => (
        <MultipleTagItem tags={tags} key={index} tag={tag} index={index} />
      ))}
    </>
  )
}

const Badges: React.FC<TagsProps> = ({ colorHex, hasAccess, tags }) => (
  <>
    <SingleTag tags={tags} colorHex={colorHex} hasAccess={hasAccess} />
    <MultipleTags tags={tags} />
  </>
)

// A click on the chips must not select the page: the chips open the tags
// popover on their own, the page toolbar stays away.
const stop = (event: React.SyntheticEvent) => event.stopPropagation()

interface ChipsProps extends React.HTMLAttributes<HTMLDivElement> {
  id: string
  onActive: (active: boolean) => void
}

// The chips are the trigger of the popover: the popover hands its click and
// aria props here, a click anywhere on the chips toggles it.
const Chips = forwardRef<HTMLDivElement, ChipsProps>(
  ({ children, id, onActive, ...trigger }, ref) => {
    const { active } = TinyPopover.useTrigger()
    useTagsGuard(id, active)

    useEffect(() => {
      onActive(active)
    }, [active])

    return (
      <div
        {...trigger}
        className={css.container}
        onMouseDown={stop}
        onPointerDown={stop}
        ref={ref}
      >
        {children}
      </div>
    )
  }
)

export const NodeTags: React.FC<Props> = ({ id }) => {
  const tags = useSelector<AppState, TagItem[]>(
    (state) => getUsedTags(state, id),
    shallowEqual
  )

  const colorHex = useSelector(
    migrationProjectSelector.getProjectColorHexOrDefault(tags[0]?.colorId)
  )

  const { hasAccess } = useProjectAccess(TAGS_PERMISSION.SHOW)
  // The popover outlives the last unchecked tag: it stays until a click outside.
  const [opened, setOpened] = useState(false)

  if (tags.length === 0 && !opened) {
    return null
  }

  if (!hasAccess) {
    return (
      <div className={css.container}>
        <Badges colorHex={colorHex} hasAccess={hasAccess} tags={tags} />
      </div>
    )
  }

  return (
    <TagsPopover nodeId={id} nodes={[id]}>
      <Chips id={id} onActive={setOpened}>
        <Badges colorHex={colorHex} hasAccess={hasAccess} tags={tags} />
      </Chips>
    </TagsPopover>
  )
}
